import React from "react";
import { useTranslation } from "react-i18next";
import { Check, Edit2, X, Trash2, ArrowUpLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import InputText from "@/components/common/InputText";
import ErrorMessage from "@/components/common/ErrorMessage";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface TaskHeaderProps {
  taskName: string;
  isEditingName: boolean;
  setIsEditingName: (editing: boolean) => void;
  onTaskNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTaskNameSubmit: () => void;
  taskNameError?: string;
  isMilestone: boolean;
  onMilestoneToggle: (checked: boolean) => void;
  milestoneDisabled: boolean;
  parentTaskName?: string;
  allowed: boolean;
  onClose: () => void;
  onDeleteRequest?: () => void;
  canDelete?: boolean;
  onParentTaskClick?: () => void;
}

export const TaskHeader: React.FC<TaskHeaderProps> = ({
  taskName,
  isEditingName,
  setIsEditingName,
  onTaskNameChange,
  onTaskNameSubmit,
  taskNameError,
  isMilestone,
  onMilestoneToggle,
  milestoneDisabled,
  parentTaskName,
  allowed,
  onClose,
  onDeleteRequest,
  canDelete,
  onParentTaskClick,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 pb-3 border-b border-slate-100">
      {/* Top action row: Breadcrumb / Milestone pill + action buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {parentTaskName && (
            <div 
              className={`flex items-center gap-1.5 text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200/60 px-2.5 py-1 rounded-md ${onParentTaskClick ? "cursor-pointer hover:bg-slate-100 hover:border-slate-300 transition-colors" : ""}`}
              onClick={onParentTaskClick}
            >
              <ArrowUpLeft className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-slate-400">{t("parent")}:</span>
              <span className="text-slate-700 font-semibold truncate max-w-[200px]">
                {parentTaskName}
              </span>
            </div>
          )}

          {/* Milestone Checkbox */}
          <label
            className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
              milestoneDisabled || !allowed
                ? "opacity-50 cursor-not-allowed bg-slate-50 border border-slate-200 text-slate-400"
                : "cursor-pointer bg-slate-50 hover:bg-slate-100/80 border border-slate-200 text-slate-700 select-none shadow-2xs"
            }`}
          >
            <input
              type="checkbox"
              disabled={milestoneDisabled || !allowed}
              checked={isMilestone}
              onChange={(e) => onMilestoneToggle(e.target.checked)}
              className="w-3.5 h-3.5 rounded text-primary-600 focus:ring-primary-500 focus:ring-1 border-slate-300 cursor-pointer disabled:cursor-not-allowed"
            />
            <span>{t("set_as_milestone")}</span>
          </label>
        </div>

        {/* Top Right Action Icons */}
        <div className="flex items-center gap-1.5">
          {canDelete && onDeleteRequest && allowed && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="none"
                    size="sm"
                    onClick={onDeleteRequest}
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 bg-red-50/80 hover:bg-red-100/80 border border-red-100 rounded-xl transition-all shadow-2xs flex items-center justify-center cursor-pointer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">{t("delete")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  id="closeTask"
                  variant="none"
                  size="sm"
                  onClick={onClose}
                  className="h-8 w-8 p-0 text-slate-500 hover:text-slate-800 bg-slate-100/80 hover:bg-slate-200/80 border border-slate-200/60 rounded-xl transition-all shadow-2xs flex items-center justify-center cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-xs">{t("close")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Task Name Title Bar */}
      <div className="flex items-center gap-2 group min-h-[38px]">
        {isEditingName ? (
          <div className="flex-1 flex flex-col">
            <div className="flex items-center gap-1.5">
              <InputText
                autoFocus
                name="taskName"
                value={taskName}
                onChange={onTaskNameChange}
                onBlur={onTaskNameSubmit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onTaskNameSubmit();
                  } else if (e.key === "Escape") {
                    setIsEditingName(false);
                  }
                }}
                placeholder={t("create_new_task")}
                className="text-lg font-semibold text-slate-900 border-primary-500 focus:ring-1 focus:ring-primary-500 h-9"
              />
              <Button
                type="button"
                variant="none"
                size="sm"
                onClick={onTaskNameSubmit}
                className="h-8 w-8 p-0 bg-primary-50 text-primary-600 hover:bg-primary-100 rounded-md"
              >
                <Check className="w-4 h-4" />
              </Button>
            </div>
            {taskNameError && (
              <ErrorMessage className="mt-1 text-xs">
                {t(taskNameError)}
              </ErrorMessage>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1">
            <h2
              onClick={() => {
                if (allowed) setIsEditingName(true);
              }}
              className={`text-xl font-bold text-slate-900 tracking-tight transition-colors ${
                allowed ? "hover:text-primary-600 cursor-pointer" : ""
              }`}
            >
              {taskName || t("create_new_task")}
            </h2>
            {allowed && (
              <Button
                type="button"
                variant="none"
                size="sm"
                onClick={() => setIsEditingName(true)}
                className="opacity-0 group-hover:opacity-100 h-7 w-7 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-all"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskHeader;
